import { CalendarClock, Plus, X } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'react-toastify'
import {
	useCreateCommitmentException,
	useDeleteCommitmentException,
	useUpdateCommitmentExceptionStatus,
} from '@/api/queries/commitment-matrix-queries'
import {
	CommitmentExceptionFormContent,
	type CommitmentExceptionFormValues,
} from '@/components/administration/commitment-matrix/commitment-matrix-exception-form'
import {
	ConfirmDialog,
	ResponsiveFormModal,
	SearchInput,
} from '@/components/commons'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { useProfileStore } from '@/store'
import type {
	CommitmentException,
	CommitmentExceptionReason,
	CommitmentExceptionStatus,
	PersonListItem,
} from '@/types'
import { getApiErrorMessage } from '@/utils/api-error'

const NO_BRANCH_MESSAGE =
	'Selecione uma filial no seu perfil para ver e decidir as justificativas.'

const REASON_LABELS: Record<CommitmentExceptionReason, string> = {
	FERIAS: 'Férias',
	ATESTADO: 'Atestado',
	AFASTAMENTO: 'Afastamento',
	TRANSFERIDO: 'Transferido',
	FOLGA_DE_CAMPO: 'Folga de campo',
	EM_MOBILIZACAO: 'Em mobilização',
	LICENCA_MATERNIDADE: 'Licença maternidade',
	OUTRO: 'Outro',
}

const STATUS_LABELS: Record<CommitmentExceptionStatus, string> = {
	PENDENTE: 'Pendente',
	APROVADO: 'Aprovado',
	REJEITADO: 'Rejeitado',
}

const STATUS_BADGE_CLASS: Record<CommitmentExceptionStatus, string> = {
	PENDENTE: 'bg-kaefer-honey-yellow/20 text-kaefer-dark-grey',
	APROVADO: 'bg-kaefer-grass-green/15 text-kaefer-forest-green',
	REJEITADO: 'bg-kaefer-red/15 text-kaefer-red',
}

interface PendingTabProps {
	exceptions: CommitmentException[]
	isLoading: boolean
	personById: Map<string, PersonListItem>
	scopeLabel: string | null
	personName: string
	onPersonNameChange: (value: string) => void
}

export function PendingTab({
	exceptions,
	isLoading,
	personById,
	scopeLabel,
	personName,
	onPersonNameChange,
}: PendingTabProps) {
	const { defaultBranchId, defaultBranchName } = useProfileStore()
	const createExceptionMutation = useCreateCommitmentException()
	const updateExceptionStatusMutation = useUpdateCommitmentExceptionStatus()
	const deleteExceptionMutation = useDeleteCommitmentException()

	const [exceptionFormOpen, setExceptionFormOpen] = useState(false)
	const [cancelingException, setCancelingException] =
		useState<CommitmentException | null>(null)

	const handleSaveException = (values: CommitmentExceptionFormValues) => {
		createExceptionMutation.mutate(values, {
			onSuccess: () => {
				toast.success('Exceção registrada.')
				setExceptionFormOpen(false)
			},
			onError: (error) => toast.error(getApiErrorMessage(error)),
		})
	}

	const handleUpdateExceptionStatus = (
		id: string,
		status: Extract<CommitmentExceptionStatus, 'APROVADO' | 'REJEITADO'>,
	) => {
		// a decisão só vale para colaboradores da filial selecionada no perfil
		if (!defaultBranchId) {
			toast.error(NO_BRANCH_MESSAGE)
			return
		}

		updateExceptionStatusMutation.mutate(
			{ id, body: { status, activeBranchId: defaultBranchId } },
			{
				onError: (error) => toast.error(getApiErrorMessage(error)),
			},
		)
	}

	const isRevoking = cancelingException?.status === 'APROVADO'

	const handleConfirmCancelException = () => {
		if (!cancelingException) return

		if (isRevoking && !defaultBranchId) {
			toast.error(NO_BRANCH_MESSAGE)
			return
		}

		deleteExceptionMutation.mutate(
			{
				id: cancelingException.id,
				activeBranchId: defaultBranchId || undefined,
			},
			{
				onSuccess: () => {
					toast.success(isRevoking ? 'Abono revogado.' : 'Exceção cancelada.')
					setCancelingException(null)
				},
				onError: (error) => {
					toast.error(getApiErrorMessage(error))
					setCancelingException(null)
				},
			},
		)
	}

	return (
		<div className="flex flex-col gap-4">
			<div className="flex flex-wrap items-center justify-between gap-3">
				<p className="text-xs text-muted-foreground">
					Justificativas (férias, atestado, afastamento) enviadas pelo
					colaborador ou registradas pelo admin, aguardando aprovação.
				</p>
				<Button
					onClick={() => setExceptionFormOpen(true)}
					className="gap-2 bg-kaefer-medium-grey text-white hover:bg-kaefer-medium-grey/90 w-full sm:w-auto"
				>
					<Plus className="size-4" />
					<span>Registrar exceção</span>
				</Button>
			</div>

			<p className="-mt-2 text-xs text-kaefer-medium-grey">
				{defaultBranchId
					? `Exibindo apenas colaboradores da filial selecionada no seu perfil: ${defaultBranchName}.`
					: NO_BRANCH_MESSAGE}
			</p>

			<SearchInput
				value={personName}
				onChange={onPersonNameChange}
				placeholder="Buscar por nome do colaborador..."
				className="w-full sm:max-w-xs"
			/>

			<div className="flex flex-col gap-3">
				{isLoading ? (
					Array.from({ length: 3 }).map((_, i) => (
						<Skeleton
							key={`exception-skeleton-${i}`}
							className="h-20 w-full rounded-lg"
						/>
					))
				) : exceptions.length === 0 ? (
					<p className="py-10 text-center text-sm text-muted-foreground">
						Nenhuma exceção registrada{scopeLabel ? ' para este foco' : ''}.
					</p>
				) : (
					exceptions
						.slice()
						.sort((a: CommitmentException, b: CommitmentException) =>
							a.status === b.status ? 0 : a.status === 'PENDENTE' ? -1 : 1,
						)
						.map((exception) => {
							const person = personById.get(exception.personId)
							return (
								<div
									key={exception.id}
									className="rounded-lg border border-kaefer-light-silver bg-white p-4"
								>
									<div className="flex flex-wrap items-start justify-between gap-3">
										<div className="min-w-0">
											<span className="font-semibold text-kaefer-dark-grey">
												{person?.name ?? 'Colaborador não encontrado'}
											</span>
											<div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-kaefer-medium-grey">
												<span className="flex items-center gap-1.5">
													<CalendarClock className="size-3.5" />
													{formatDate(exception.startDate)} —{' '}
													{formatDate(exception.endDate)}
												</span>
												{person && (
													<span>
														Matrícula {person.badge} · CR {person.crCode} —{' '}
														{person.crName}
													</span>
												)}
											</div>
											<div className="mt-1 text-xs text-kaefer-medium-grey">
												Solicitado por {exception.requestedByName}
												{exception.reviewedByName &&
													exception.reviewedAt &&
													` · ${STATUS_LABELS[exception.status]} por ${exception.reviewedByName} em ${formatDateTime(exception.reviewedAt)}`}
											</div>
										</div>

										<div className="flex items-center gap-2">
											<Badge className="bg-kaefer-light-silver/70 text-kaefer-dark-grey">
												{REASON_LABELS[exception.reason]}
											</Badge>
											<Badge className={STATUS_BADGE_CLASS[exception.status]}>
												{STATUS_LABELS[exception.status]}
											</Badge>
											{(exception.status === 'PENDENTE' ||
												exception.status === 'APROVADO') && (
												<Button
													type="button"
													variant="ghost"
													size="icon-circle"
													onClick={() => setCancelingException(exception)}
													disabled={deleteExceptionMutation.isPending}
													className="text-kaefer-red hover:bg-transparent hover:text-kaefer-red/70"
													title={
														exception.status === 'APROVADO'
															? 'Revogar abono'
															: 'Cancelar pedido'
													}
												>
													<X className="size-4" />
												</Button>
											)}
										</div>
									</div>

									{exception.status === 'PENDENTE' && (
										<div className="mt-3 flex gap-2">
											<Button
												type="button"
												size="sm"
												variant="cancel"
												disabled={updateExceptionStatusMutation.isPending}
												onClick={() =>
													handleUpdateExceptionStatus(exception.id, 'REJEITADO')
												}
											>
												Rejeitar
											</Button>
											<Button
												type="button"
												size="sm"
												disabled={updateExceptionStatusMutation.isPending}
												className="bg-kaefer-grass-green text-white hover:bg-kaefer-grass-green/90"
												onClick={() =>
													handleUpdateExceptionStatus(exception.id, 'APROVADO')
												}
											>
												Aprovar
											</Button>
										</div>
									)}
								</div>
							)
						})
				)}
			</div>

			<ResponsiveFormModal
				open={exceptionFormOpen}
				onOpenChange={setExceptionFormOpen}
				title="Registrar exceção"
				dialogClassName="max-h-[85vh] overflow-y-auto sm:max-w-md"
			>
				<CommitmentExceptionFormContent
					onSave={handleSaveException}
					onClose={() => setExceptionFormOpen(false)}
					isSaving={createExceptionMutation.isPending}
				/>
			</ResponsiveFormModal>

			<ConfirmDialog
				open={!!cancelingException}
				onOpenChange={(open) => {
					if (!open) setCancelingException(null)
				}}
				title={isRevoking ? 'Revogar abono' : 'Cancelar pedido'}
				onConfirm={handleConfirmCancelException}
				isPending={deleteExceptionMutation.isPending}
			>
				{isRevoking
					? 'Este abono já foi aprovado. Ao revogar, o período volta a contar para a meta do colaborador. Deseja continuar?'
					: 'Tem certeza que deseja cancelar este pedido de exceção?'}
			</ConfirmDialog>
		</div>
	)
}

function formatDate(iso: string): string {
	const [year, month, day] = iso.split('-')
	return `${day}/${month}/${year}`
}

function formatDateTime(iso: string): string {
	return new Date(iso).toLocaleString('pt-BR', {
		day: '2-digit',
		month: '2-digit',
		year: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
	})
}
